import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { Call, Disposition, Lead } from '@marnic/shared';
import { formatDuration } from '@marnic/shared';
import { useSoftphone } from '../store/softphone';

interface Props {
  call: Call;
  lead: Lead | null;
  campaignLeadId?: string;
  onClose: () => void;
  onSaved: () => void;
}

type CrmDestinationMatch = 'lead' | 'cold_lead' | 'contact' | 'customer' | null;
interface CrmDestinationOptions {
  matchType: CrmDestinationMatch;
  coldLeadId: number | null;
  leadId: number | null;
}
type CrmDestination = 'auto' | 'cold' | 'lead' | 'promote';

export function DispositionModal({ call, lead, campaignLeadId, onClose, onSaved }: Props): JSX.Element {
  const dispositions = useQuery({
    queryKey: ['dispositions'],
    queryFn: () => api.get<Disposition[]>('/dispositions'),
  });
  const destinationOptions = useQuery({
    queryKey: ['call-crm-destination-options', call.id],
    queryFn: () => api.get<CrmDestinationOptions>(`/calls/${call.id}/crm-destination-options`),
  });
  const linkedCallbackId = useSoftphone((s) => s.currentCallbackId);
  const [code, setCode] = useState<string>('');
  // Verify (and correct) the name of the person we dialed. Seeded from the
  // matched lead; edits are pushed back to ho.marn.uk on save.
  const originalName = lead ? `${lead.firstName} ${lead.lastName}`.trim() : '';
  const [verifiedName, setVerifiedName] = useState<string>(originalName);
  const [notes, setNotes] = useState<string>(call.notes ?? '');
  const [rating, setRating] = useState<number | null>(null);
  const [followUpEnabled, setFollowUpEnabled] = useState(false);
  const [followUpAt, setFollowUpAt] = useState<string>('');
  // Default destination = 'auto' (current logic). Switches when the agent
  // explicitly picks a destination. For an existing cold-lead match, only
  // 'auto' (= leave as cold) and 'promote' are offered.
  const [crmDestination, setCrmDestination] = useState<CrmDestination>('auto');
  // Agent-typed identity for the new lead/cold-lead. Sent only when the
  // counterparty has no existing CRM match — the server ignores them
  // otherwise. Empty strings collapse to "use LLM-extracted identity".
  const [crmAgentName, setCrmAgentName] = useState<string>('');
  const [crmAgentCompany, setCrmAgentCompany] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selected = dispositions.data?.find((d) => d.code === code);

  useEffect(() => {
    if (!code && dispositions.data && dispositions.data.length > 0) {
      setCode(dispositions.data[0]!.code);
    }
  }, [dispositions.data, code]);

  useEffect(() => {
    if (selected?.triggersCallback) setFollowUpEnabled(true);
  }, [selected?.triggersCallback]);

  const submit = async (): Promise<void> => {
    if (!code) {
      setError('Choose a disposition');
      return;
    }
    if (selected?.requiresNotes && !notes.trim()) {
      setError('Notes required for this outcome');
      return;
    }
    const shouldScheduleFollowUp = followUpEnabled || !!selected?.triggersCallback;
    if (shouldScheduleFollowUp && !followUpAt) {
      setError('Pick a follow-up date and time');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // Persist a corrected contact name back to the lead + ho.marn.uk before
      // filing the disposition. Non-fatal — a CRM hiccup shouldn't block the
      // disposition save.
      if (lead) {
        const trimmed = verifiedName.trim();
        if (trimmed && trimmed !== originalName) {
          const [firstName, ...rest] = trimmed.split(/\s+/);
          await api
            .patch(`/leads/${lead.id}`, { firstName, lastName: rest.join(' ') })
            .catch((e) => console.warn('[disposition] name sync failed', e));
        }
      }

      await api.post(`/calls/${call.id}/disposition`, {
        code,
        notes: notes.trim() || undefined,
        rating: rating ?? undefined,
        ...(shouldScheduleFollowUp
          ? {
              followUpAction: 'schedule',
              followUpScheduledFor: new Date(followUpAt).toISOString(),
              followUpDate: followUpAt.slice(0, 10),
            }
          : { followUpAction: 'none' }),
        campaignLeadId,
        crmDestination,
        // Only forward identity overrides for the no-match path; the server
        // also ignores them otherwise, but skipping the round-trip is tidier.
        ...(destinationOptions.data?.matchType == null
          ? {
              crmAgentName: crmAgentName.trim() || undefined,
              crmAgentCompany: crmAgentCompany.trim() || undefined,
            }
          : {}),
      });
      onSaved();
    } catch (err: unknown) {
      // Surface the underlying cause in the console so DB / validation errors
      // (e.g. an un-applied migration) don't disappear behind a generic banner.
      console.warn('[disposition] save failed', err);
      const e = err as {
        code?: string;
        message?: string;
        error?: string;
        detail?: { error?: string };
      };
      setError(e.detail?.error ?? e.error ?? e.message ?? e.code ?? 'Could not save disposition');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-ink-900/40 p-0 sm:p-4">
      <div className="card grid w-full max-w-2xl grid-rows-[auto_1fr_auto] max-h-[100dvh] sm:max-h-[90vh] sm:rounded-lg rounded-t-2xl sm:rounded-b-lg">
        <header className="flex items-start justify-between gap-3 border-b border-ink-200 p-4 shrink-0">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold">Call complete</h3>
            {lead ? (
              <div className="mt-1">
                <label className="text-[11px] uppercase tracking-wide text-ink-400">
                  Who did we speak to?
                </label>
                <div className="flex items-center gap-2">
                  <input
                    className="input h-8 py-1 text-sm"
                    value={verifiedName}
                    onChange={(e) => setVerifiedName(e.target.value)}
                    placeholder="Contact name"
                    aria-label="Verify the name of the person dialed"
                  />
                  {lead.company && (
                    <span className="text-xs text-ink-500 truncate">· {lead.company}</span>
                  )}
                </div>
                {verifiedName.trim() !== originalName && (
                  <p className="text-[11px] text-brand-600 mt-0.5">Name edit will sync to ho.marn.uk.</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-ink-500 truncate">{call.toNumber}</p>
            )}
            <p className="text-xs text-ink-400 mt-1">
              Duration {formatDuration(call.durationSeconds ?? 0)} · Recording stored
            </p>
            {linkedCallbackId && (
              <p className="text-xs text-brand-700 mt-1">
                ↩ Linked to a scheduled callback — closing this disposition will resolve it.
              </p>
            )}
          </div>
          <button type="button" className="btn-ghost shrink-0" onClick={onClose} aria-label="Close">✕</button>
        </header>

        <div className="scroll-area space-y-4 p-4 min-h-0">
          <div>
            <label className="label">Disposition</label>
            <div className="space-y-1.5">
              {dispositions.data?.map((d) => (
                <label
                  key={d.code}
                  className={`flex items-start gap-3 rounded-md border p-2 cursor-pointer ${
                    code === d.code ? 'border-brand-500 bg-brand-50' : 'border-ink-200 hover:bg-ink-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="disposition"
                    className="mt-1"
                    checked={code === d.code}
                    onChange={() => setCode(d.code)}
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium">{d.label}</div>
                    <div className="text-xs text-ink-500">
                      {d.category}
                      {d.requiresNotes && ' · notes required'}
                      {d.triggersCallback && ' · schedule callback'}
                      {d.triggersDnc && ' · adds to DNC'}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="label">Notes</label>
            <textarea
              className="input min-h-[120px]"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <div>
            <label className="label">Quick rating</label>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  className={`text-2xl ${(rating ?? 0) >= star ? 'text-amber-400' : 'text-ink-300'}`}
                  onClick={() => setRating(star)}
                >
                  ★
                </button>
              ))}
              {rating != null && (
                <button type="button" className="btn-ghost ml-2" onClick={() => setRating(null)}>
                  Clear
                </button>
              )}
            </div>
          </div>

          <CrmDestinationControl
            options={destinationOptions.data}
            loading={destinationOptions.isLoading}
            value={crmDestination}
            onChange={setCrmDestination}
            agentName={crmAgentName}
            onAgentNameChange={setCrmAgentName}
            agentCompany={crmAgentCompany}
            onAgentCompanyChange={setCrmAgentCompany}
          />

          <div>
            <label className="label">Follow-up call</label>
            <label className="flex items-center gap-2 rounded-md border border-ink-200 p-2 text-sm">
              <input
                type="checkbox"
                checked={followUpEnabled || !!selected?.triggersCallback}
                disabled={!!selected?.triggersCallback}
                onChange={(e) => setFollowUpEnabled(e.target.checked)}
              />
              <span>Schedule a follow-up call</span>
            </label>
            {(followUpEnabled || selected?.triggersCallback) && (
              <div className="mt-2">
                <input
                  type="datetime-local"
                  className="input text-sm"
                  value={followUpAt}
                  onChange={(e) => setFollowUpAt(e.target.value)}
                />
                <p className="text-xs text-ink-500 mt-1">
                  Choose the exact date and time agreed with the prospect.
                </p>
              </div>
            )}
          </div>

          {error && (
            <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
          )}
        </div>

        <footer className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 border-t border-ink-200 p-4 shrink-0">
          <button type="button" className="btn-secondary w-full sm:w-auto" onClick={onClose}>
            Skip for now
          </button>
          <button type="button" className="btn-primary w-full sm:w-auto" onClick={submit} disabled={busy}>
            {busy ? 'Saving…' : 'Save & continue'}
          </button>
        </footer>
      </div>
    </div>
  );
}

function CrmDestinationControl(props: {
  options: CrmDestinationOptions | null | undefined;
  loading: boolean;
  value: CrmDestination;
  onChange: (next: CrmDestination) => void;
  agentName: string;
  onAgentNameChange: (next: string) => void;
  agentCompany: string;
  onAgentCompanyChange: (next: string) => void;
}): JSX.Element | null {
  const { options, loading, value, onChange, agentName, onAgentNameChange, agentCompany, onAgentCompanyChange } = props;

  if (loading) {
    return (
      <div>
        <label className="label">CRM destination</label>
        <p className="text-xs text-ink-500">Checking ho.marn.uk for an existing match…</p>
      </div>
    );
  }
  if (!options) return null;

  // Already linked to a real lead/contact/customer — nothing for the agent
  // to choose. Show a passive confirmation so they know where the call will
  // be filed in Perfex.
  if (options.matchType === 'lead' || options.matchType === 'contact' || options.matchType === 'customer') {
    const label = options.matchType === 'lead' ? 'lead' : options.matchType;
    return (
      <div>
        <label className="label">CRM destination</label>
        <p className="rounded-md border border-ink-200 bg-ink-50 px-3 py-2 text-sm text-ink-700">
          Already matches a Perfex {label} — call notes will attach there automatically.
        </p>
      </div>
    );
  }

  // Existing cold lead — only meaningful action is to promote it.
  if (options.matchType === 'cold_lead') {
    return (
      <div>
        <label className="label">CRM destination</label>
        <label className="flex items-start gap-2 rounded-md border border-ink-200 p-2 text-sm">
          <input
            type="checkbox"
            checked={value === 'promote'}
            onChange={(e) => onChange(e.target.checked ? 'promote' : 'auto')}
          />
          <span>
            <span className="font-medium">Promote to lead</span>
            <span className="block text-xs text-ink-500">
              This number is currently in the cold-list. Tick to convert it into a real Perfex lead.
            </span>
          </span>
        </label>
      </div>
    );
  }

  // No match yet — offer auto / cold / lead, plus optional identity overrides.
  const choices: Array<{ id: CrmDestination; label: string; hint: string }> = [
    { id: 'auto', label: 'Auto (default)', hint: 'Save to the cold-list (current behaviour).' },
    { id: 'cold', label: 'Cold lead', hint: 'Force-create in the Perfex cold-list.' },
    { id: 'lead', label: 'Lead', hint: 'Create as a real Perfex lead.' },
  ];
  return (
    <div className="space-y-3">
      <div>
        <label className="label">CRM destination</label>
        <div className="space-y-1.5">
          {choices.map((c) => (
            <label
              key={c.id}
              className={`flex items-start gap-3 rounded-md border p-2 cursor-pointer ${
                value === c.id ? 'border-brand-500 bg-brand-50' : 'border-ink-200 hover:bg-ink-50'
              }`}
            >
              <input
                type="radio"
                name="crm-destination"
                className="mt-1"
                checked={value === c.id}
                onChange={() => onChange(c.id)}
              />
              <div className="flex-1">
                <div className="text-sm font-medium">{c.label}</div>
                <div className="text-xs text-ink-500">{c.hint}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div>
        <label className="label">Lead identity (optional)</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <input
            type="text"
            className="input text-sm"
            placeholder="Lead name"
            value={agentName}
            maxLength={191}
            onChange={(e) => onAgentNameChange(e.target.value)}
          />
          <input
            type="text"
            className="input text-sm"
            placeholder="Company"
            value={agentCompany}
            maxLength={191}
            onChange={(e) => onAgentCompanyChange(e.target.value)}
          />
        </div>
        <p className="text-xs text-ink-500 mt-1">
          Used only when there's no existing CRM record. Leave blank to fall back to the AI-extracted name.
        </p>
      </div>
    </div>
  );
}
